import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  add_allergy,
  add_condition,
  add_vaccination,
  create_pet,
  delete_allergy,
  delete_condition,
  delete_pet,
  delete_pet_photo,
  delete_vaccination,
  fetch_allergies,
  fetch_breeds,
  fetch_conditions,
  fetch_pet,
  fetch_pet_medical_records,
  fetch_pet_photos,
  fetch_pets,
  fetch_vaccinations,
  set_primary_pet_photo,
  update_pet,
  upload_pet_photo,
} from './api';
import type {
  CreateAllergyRequest,
  CreateConditionRequest,
  CreateVaccinationRequest,
  PetSpecies,
  PetWriteRequest,
} from './types';

const PETS_KEY = ['pets'] as const;

export function use_pets() {
  return useQuery({ queryKey: PETS_KEY, queryFn: fetch_pets, staleTime: 0 });
}

export function use_pet(id: string | undefined) {
  return useQuery({
    queryKey: ['pet', id],
    queryFn: () => fetch_pet(id as string),
    enabled: !!id,
    staleTime: 0,
  });
}

export function use_breeds(species: PetSpecies | undefined) {
  return useQuery({
    queryKey: ['breeds', species ?? 'all'],
    queryFn: () => fetch_breeds(species),
    staleTime: 5 * 60 * 1000,
    enabled: !!species,
  });
}

export function use_create_pet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PetWriteRequest) => create_pet(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: PETS_KEY }),
  });
}

export function use_update_pet(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PetWriteRequest) => update_pet(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PETS_KEY });
      qc.invalidateQueries({ queryKey: ['pet', id] });
    },
  });
}

export function use_delete_pet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => delete_pet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: PETS_KEY }),
  });
}

// ---- Photos ----
export function use_pet_photos(pet_id: string | undefined) {
  return useQuery({
    queryKey: ['pet-photos', pet_id],
    queryFn: () => fetch_pet_photos(pet_id as string),
    enabled: !!pet_id,
    staleTime: 0,
  });
}

export function use_pet_photo_mutations(pet_id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pet-photos', pet_id] });
    qc.invalidateQueries({ queryKey: PETS_KEY }); // primary thumb on cards
  };
  const upload = useMutation({
    mutationFn: (file: File) => upload_pet_photo(pet_id, file),
    onSuccess: invalidate,
  });
  const set_primary = useMutation({
    mutationFn: (photo_id: string) => set_primary_pet_photo(pet_id, photo_id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (photo_id: string) => delete_pet_photo(pet_id, photo_id),
    onSuccess: invalidate,
  });
  return { upload, set_primary, remove };
}

// ---- Medical records ----
/** Read-only vet-authored clinical records (finalized) for a pet. */
export function use_pet_medical_records(pet_id: string | undefined) {
  return useQuery({
    queryKey: ['pet-medical-records', pet_id],
    queryFn: () => fetch_pet_medical_records(pet_id as string),
    enabled: !!pet_id,
  });
}

export function use_pet_medical(pet_id: string) {
  const qc = useQueryClient();
  const enabled = !!pet_id;

  const vaccinations = useQuery({
    queryKey: ['pet-vaccinations', pet_id],
    queryFn: () => fetch_vaccinations(pet_id),
    enabled,
    staleTime: 0,
  });
  const allergies = useQuery({
    queryKey: ['pet-allergies', pet_id],
    queryFn: () => fetch_allergies(pet_id),
    enabled,
    staleTime: 0,
  });
  const conditions = useQuery({
    queryKey: ['pet-conditions', pet_id],
    queryFn: () => fetch_conditions(pet_id),
    enabled,
    staleTime: 0,
  });

  const inval = (key: string) => () => qc.invalidateQueries({ queryKey: [key, pet_id] });

  return {
    vaccinations,
    allergies,
    conditions,
    add_vaccination: useMutation({
      mutationFn: (input: CreateVaccinationRequest) => add_vaccination(pet_id, input),
      onSuccess: inval('pet-vaccinations'),
    }),
    remove_vaccination: useMutation({
      mutationFn: (id: string) => delete_vaccination(pet_id, id),
      onSuccess: inval('pet-vaccinations'),
    }),
    add_allergy: useMutation({
      mutationFn: (input: CreateAllergyRequest) => add_allergy(pet_id, input),
      onSuccess: inval('pet-allergies'),
    }),
    remove_allergy: useMutation({
      mutationFn: (id: string) => delete_allergy(pet_id, id),
      onSuccess: inval('pet-allergies'),
    }),
    add_condition: useMutation({
      mutationFn: (input: CreateConditionRequest) => add_condition(pet_id, input),
      onSuccess: inval('pet-conditions'),
    }),
    remove_condition: useMutation({
      mutationFn: (id: string) => delete_condition(pet_id, id),
      onSuccess: inval('pet-conditions'),
    }),
  };
}
